// @tennis-matches/services/tournament — Ort & Zielgruppe für Turnier-Setup / offene Anmeldung

function __asStr(v) {
  return String(v ?? "").trim();
}

/** @typedef {{ clubName?: string, city?: string, postalCode?: string }} ClubSnapshot */
/** @typedef {{ useClubDefault: boolean, customName: string, customAddress: string, clubSnapshot?: ClubSnapshot|null }} TournamentVenue */
/** @typedef {{ gender: string, ageClassLabel: string }} TournamentAudience */

export const MATCHMIXER_AUDIENCE_GENDERS = ["ladies", "men", "mixed"];

const LEGACY_AGE_GROUP_IDS = ["u10", "u12", "u14", "u16", "u18", "adult", "seniors"];
const GENDER_SET = new Set(MATCHMIXER_AUDIENCE_GENDERS);

export function defaultTournamentVenue() {
  return {
    useClubDefault: true,
    customName: "",
    customAddress: "",
    clubSnapshot: null,
  };
}

export function defaultTournamentAudience() {
  return {
    gender: "mixed",
    ageClassLabel: "",
  };
}

export function normalizeTournamentVenue(raw) {
  const base = defaultTournamentVenue();
  if (!raw || typeof raw !== "object") return base;
  const snap = raw.clubSnapshot;
  return {
    useClubDefault: raw.useClubDefault !== false,
    customName: __asStr(raw.customName),
    customAddress: __asStr(raw.customAddress),
    clubSnapshot:
      snap && typeof snap === "object"
        ? {
            clubName: __asStr(snap.clubName),
            city: __asStr(snap.city),
            postalCode: __asStr(snap.postalCode),
          }
        : null,
  };
}

function __migrateLegacyAgeLabel(raw, t) {
  const direct = __asStr(raw.ageClassLabel || raw.ageClass);
  if (direct) return direct;

  const legacy = Array.isArray(raw?.ageGroups) ? raw.ageGroups.map(__asStr).filter(Boolean) : [];
  if (legacy.length > 0) {
    const known = legacy.filter((id) => LEGACY_AGE_GROUP_IDS.includes(id));
    const labels = known.map((id) =>
      typeof t === "function" ? t(`matchmixer.setup.audience.ageGroup.${id}`) : id.toUpperCase()
    );
    const extra = legacy.filter((id) => !LEGACY_AGE_GROUP_IDS.includes(id));
    return [...labels, ...extra].join(", ");
  }

  if (raw.youth === true) {
    const youth = typeof t === "function" ? t("matchmixer.setup.audience.youth") : "Jugend";
    return youth;
  }

  return "";
}

export function normalizeTournamentAudience(raw, t) {
  const base = defaultTournamentAudience();
  if (!raw || typeof raw !== "object") return base;

  let gender = __asStr(raw.gender).toLowerCase();
  if (gender === "open" || !GENDER_SET.has(gender)) {
    gender = "mixed";
  }

  return {
    gender,
    ageClassLabel: __migrateLegacyAgeLabel(raw, t),
  };
}

/**
 * Lesbare Club-Adresse aus Profil/RPC (ohne Straße — club_profiles hat nur Ort/PLZ).
 */
export function formatClubVenueDisplay({ clubName = "", city = "", postalCode = "" } = {}) {
  const name = __asStr(clubName);
  const c = __asStr(city);
  const plz = __asStr(postalCode);
  const locality = [plz, c].filter(Boolean).join(" ");
  if (name && locality) return `${name}, ${locality}`;
  return name || locality || "";
}

/**
 * @param {TournamentVenue} venue
 * @param {string} [clubFallbackLabel] — aktueller Verein aus RPC
 */
export function resolveVenueDisplayLabel(venue, clubFallbackLabel = "") {
  const v = normalizeTournamentVenue(venue);
  if (v.useClubDefault) {
    const snap = v.clubSnapshot;
    const fromSnap = snap ? formatClubVenueDisplay(snap) : "";
    return fromSnap || __asStr(clubFallbackLabel) || "";
  }
  const name = v.customName;
  const addr = v.customAddress;
  if (name && addr) return `${name} — ${addr}`;
  return name || addr || "";
}

/**
 * @param {(key: string, params?: object) => string} t
 * @param {TournamentAudience} audience
 */
export function formatAudienceSummary(t, audience) {
  const a = normalizeTournamentAudience(audience, t);
  const genderKey = `matchmixer.setup.audience.gender.${a.gender}`;
  const genderLabel = typeof t === "function" ? t(genderKey) : a.gender;
  if (a.ageClassLabel) {
    return `${genderLabel} · ${a.ageClassLabel}`;
  }
  return typeof t === "function"
    ? `${genderLabel} · ${t("matchmixer.setup.audience.ageOpen")}`
    : genderLabel;
}

/**
 * @param {(key: string, params?: object) => string} t
 * @param {TournamentVenue} venue
 * @param {string} [clubFallbackLabel]
 */
export function formatVenueSummary(t, venue, clubFallbackLabel = "") {
  const v = normalizeTournamentVenue(venue);
  if (v.useClubDefault) {
    const label = resolveVenueDisplayLabel(v, clubFallbackLabel);
    return label || (typeof t === "function" ? t("matchmixer.setup.venue.clubDefault") : "");
  }
  const custom = resolveVenueDisplayLabel(v, "");
  return custom || (typeof t === "function" ? t("matchmixer.setup.venue.customEmpty") : "");
}

export function venueToPersistPayload(venue) {
  const v = normalizeTournamentVenue(venue);
  return {
    useClubDefault: v.useClubDefault,
    customName: v.customName,
    customAddress: v.customAddress,
    clubSnapshot: v.clubSnapshot,
  };
}

export function audienceToPersistPayload(audience, t) {
  return normalizeTournamentAudience(audience, t);
}
